import { FC, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useInView } from 'react-intersection-observer';
import * as motion from 'motion/react-client';
import {
  usePopupState,
  bindMenu,
  bindTrigger,
} from 'material-ui-popup-state/hooks';
import { Menu, MenuItem, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { MoreIcon } from '@ui/icons';
import { TinPreview } from '@core/features/artworks';
import { useBasketStore, useBasketLimit } from '@customer/features/basket';

dayjs.extend(relativeTime);

import { useGetArtwork } from '../hooks';

type Props = {
  id: string;
};

export const ArtworkListItem: FC<Props> = ({ id }) => {
  const { t } = useTranslation();
  const addPosition = useBasketStore((state) => state.addPosition);
  const { maxOrderQuantity } = useBasketLimit();
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '0px 0px 500px 0px',
  });
  const { data } = useGetArtwork(id, { enabled: inView });

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'artworkMenuPopup',
  });
  const iconRef = useRef<HTMLButtonElement | null>(null);

  const handleOrderAgain = () => {
    popupState.close();
    if (!data) return;
    if (addPosition(data.id, maxOrderQuantity)) {
      toast.success(t('basketAddedToCart'));
    } else {
      toast.error(t('basketLimitReached', { limit: maxOrderQuantity }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.4,
      }}
    >
      <Box
        ref={ref}
        sx={{
          padding: { xs: 1, sm: 1, md: 8, lg: 8 },
          position: 'relative',
          zIndex: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        onClick={() => {
          if (iconRef.current) {
            if (popupState.isOpen) return;
            popupState.setAnchorEl(iconRef.current);
            popupState.open();
          }
        }}
      >
        <TinPreview image={data?.thumbnail ?? ''} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography>{t('created')}</Typography>
          <Typography
            sx={{ fontSize: 18, fontWeight: 600, textTransform: 'uppercase' }}
          >
            {dayjs(data?.created_at).format('DD.MM.YYYY')}
          </Typography>
        </Box>

        <>
          <IconButton
            ref={iconRef}
            {...bindTrigger(popupState)}
            color="primary"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <MoreIcon />
          </IconButton>
          <Menu
            {...bindMenu(popupState)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleOrderAgain}>{t('orderAgain')}</MenuItem>
          </Menu>
        </>
      </Box>
    </motion.div>
  );
};
